import Alert from "@mui/material/Alert";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/providers";
import {
  useAgentDirectory,
  type AgentDirectory,
} from "@/shared/domain/agents/hooks";
import {
  AgentThreadStatus,
  ThreadVisibility,
  type AgentThreadRecord,
} from "@/shared/domain/agentThread";
import { useAgentThreadSearch } from "@/shared/domain/agentThread/hooks";
import {
  DataTable,
  colId,
  composeFilters,
  createColumnHelper,
  useDataTableState,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type Filter,
  type FilterDefinition,
  type ICellRendererParams,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./ThreadsTab.module.css";
import { ThreadStatusBadge } from "./ThreadStatusBadge";

const columnHelper = createColumnHelper<AgentThreadRecord>();

/**
 * Build the column array for the Threads table.
 *
 * Takes a ``directoryRef`` rather than the directory itself so the
 * column array reference can be stable across renders even when the
 * agent directory loads asynchronously. AG Grid treats a new column
 * array as a structural change — it re-applies ``ColDef.hide`` defaults
 * (clobbering the user's column-visibility toggle) and rebuilds sort
 * indicators / column widths. The Agent cell renderer dereferences the
 * ref at render time, which AG Grid invokes on every visible cell, so
 * the freshly loaded directory still drives what each cell shows.
 *
 * The Title cell carries the navigation Link to
 * ``/agents/threads/:threadId``. The ``DataTable`` primitive does not
 * expose a row-click prop, so navigation lives on the cell itself.
 */
function buildColumns(directoryRef: React.MutableRefObject<AgentDirectory>) {
  return [
    // Both ``ID`` and ``Title`` link to the detail page so a row stays
    // navigable even when a thread has no title — ``DataTable`` doesn't
    // expose a row-click prop, so each cell that should be clickable has
    // to carry its own anchor.
    columnHelper.accessor("thread_id", {
      headerName: "ID",
      width: 180,
      cellClass: styles.monoCell,
      hide: false,
      cellRenderer: (p: ICellRendererParams<AgentThreadRecord, string>) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        return (
          <Link
            to={`/agents/threads/${row.thread_id}`}
            className={styles.titleLink}
          >
            {row.thread_id}
          </Link>
        );
      },
    }),
    columnHelper.accessor("title", {
      headerName: "Title",
      flex: 2,
      cellRenderer: (
        p: ICellRendererParams<AgentThreadRecord, string | undefined>,
      ) => {
        const row = p.data;
        if (!row) {
          return null;
        }
        return (
          <Link
            to={`/agents/threads/${row.thread_id}`}
            className={styles.titleLink}
          >
            {row.title ?? row.thread_id}
          </Link>
        );
      },
      hide: false,
    }),
    // "Agent" sits right after "Title" because it's primary identifying
    // context for the thread (what config was kicked off) — more
    // load-bearing than status or visibility for users skimming the
    // table. Threads started outside an agent template render an
    // em-dash; threads started via an agent that's since been deleted
    // (or paged past the directory limit) fall back to the raw agent_id
    // rather than blanking the cell — the lineage value matters even
    // when the source is gone.
    columnHelper.accessor("created_from_agent_id", {
      headerName: "Agent",
      width: 180,
      cellRenderer: (
        p: ICellRendererParams<AgentThreadRecord, string | undefined | null>,
      ) => {
        const agentId = p.value;
        if (!agentId) {
          return <span>—</span>;
        }
        const agent = directoryRef.current.byId.get(agentId);
        if (!agent) {
          // ``data-testid`` pins the fallback branch for tests:
          // asserting on text alone would pass even if this branch were
          // deleted (AG Grid's default renderer would also paint the
          // accessor value), so the test target is the element this
          // branch produces.
          return (
            <span className={styles.monoCell} data-testid="agent-cell-fallback">
              {agentId}
            </span>
          );
        }
        return (
          <Link to={`/agents/${agent.agent_id}`} className={styles.titleLink}>
            {agent.name}
          </Link>
        );
      },
      hide: false,
    }),
    columnHelper.accessor("status", {
      headerName: "Status",
      width: 160,
      cellRenderer: (
        p: ICellRendererParams<AgentThreadRecord, AgentThreadStatus>,
      ) => {
        if (!p.value) {
          return null;
        }
        return <ThreadStatusBadge status={p.value} />;
      },
      hide: false,
    }),
    columnHelper.accessor("visibility", {
      headerName: "Visibility",
      width: 120,
      // Wire values are lowercase (``private`` / ``org``) per the
      // backend's CHECK constraint; capitalize for display so the cell
      // reads naturally alongside Title and Status. The empty-string
      // fallback exists because AG Grid types ``p.value`` as
      // ``TValue | null | undefined`` regardless of ``AgentThreadRecord``
      // declaring ``visibility`` as required — the formatter contract
      // forces us to handle the framework's nullable view of the cell.
      valueFormatter: (
        p: ValueFormatterParams<AgentThreadRecord, ThreadVisibility>,
      ) => (p.value ? p.value.charAt(0).toUpperCase() + p.value.slice(1) : ""),
      hide: false,
    }),
    columnHelper.accessor("created_by", {
      headerName: "Created by",
      width: 200,
      hide: false,
    }),
    columnHelper.accessor("created", {
      headerName: "Created",
      width: 200,
      sortable: true,
      valueFormatter: (p: ValueFormatterParams<AgentThreadRecord, string>) =>
        p.value ? new Date(p.value).toLocaleString() : "",
      hide: false,
    }),
  ];
}

interface AgentThreadsTableProps {
  /**
   * Filters AND-ed onto every search regardless of what the user picks
   * in the filter widget. Use to pin the table to a single dataset
   * (``[{ field: "subject_id", comparator: "EQUALS", values: [datasetId] }]``)
   * or any other immutable slice. ``composeFilters`` wraps the user's
   * widget filters with an outer AND containing every scope entry, so
   * the user's match-mode choice between AND/OR only governs *their*
   * filters — it cannot dissolve the scope.
   *
   * Omit (default ``[]``) on the Agents > Threads listing page, which
   * shows every thread the caller is allowed to read.
   */
  scope?: Filter[];
  /**
   * Empty-state message rendered when the search returns zero rows.
   * The default copy assumes the org-wide listing; the dataset-scoped
   * mount overrides it to "No threads referencing this dataset yet."
   */
  noRowsContent?: React.ReactNode;
  /**
   * Slotted into the ``DataTable.Actions`` row after the built-in
   * filter and column-visibility controls, pushed to the right by
   * ``margin-left: auto``. Use for context-specific affordances that
   * belong next to the table chrome rather than above or below it —
   * e.g. the dataset tab's "Launch agent for this dataset" dropdown.
   * Falsy values render nothing.
   */
  trailingActions?: React.ReactNode;
}

/**
 * Threads table for the Agents page. Renders agent threads with
 * server-side filter, sort, and pagination.
 *
 * Composes a ``SearchQueryBody`` from ``tableState`` and POSTs to
 * ``v1/ai/threads/search`` via ``useAgentThreadSearch``. The wire
 * ``condition`` is built by ``composeFilters``, which AND-s the
 * caller-supplied ``scope`` onto whatever the user picked in the
 * toolbar widget. The sort field and direction come from AG Grid's
 * column header clicks via ``tableState.sort``. Only the Created
 * column declares ``sortable: true`` because the backend sort
 * whitelist is ``created`` only — surfacing the sort affordance on
 * other columns would let the user issue requests that return 400.
 *
 * Pagination uses the backend's continuation tokens. Each response
 * carries a ``next_token`` for the page after it; the component holds
 * a ``page → token`` map in state so that advancing to page N issues
 * a request with the token returned for page N-1. The map is cleared
 * when filters, sort, or page size change — those reset
 * ``currentPage`` to 0 and any held tokens reference a search shape
 * the user has moved away from.
 *
 * Failure mode: a server error replaces the table with an Alert
 * carrying the error message. An empty result set keeps the table
 * chrome and shows ``noRowsContent``.
 */
export function AgentThreadsTable({
  scope,
  noRowsContent,
  trailingActions,
}: AgentThreadsTableProps = {}) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  // Single fetch per page mount via tanstack-query's cache. Drives both
  // the per-row "Agent" cell (id → name) and the "Agent" filter
  // dropdown (labeled options) so the network cost is paid once.
  const agentDirectory = useAgentDirectory(orgId);

  // Mirror the directory into a ref so ``buildColumns`` can read the
  // latest agents without us having to re-run it. Re-running
  // ``buildColumns`` when the directory loads would hand AG Grid a fresh
  // column array, which it treats as a structural reset — re-applying
  // ``ColDef.hide`` and clobbering the user's column-visibility toggle.
  //
  // We deliberately break the codebase convention of "only write to
  // ``.current`` inside ``useEffect``" here. The cell renderer fires
  // synchronously during AG Grid's render pass — the same render in
  // which we receive the freshly loaded ``agentDirectory``. An
  // effect-based update would lag by one commit: the cell would mount
  // first (reading the previous directory), then the effect would run
  // and update the ref, and only the *next* repaint would show the
  // resolved name. ``refreshCells`` below covers that follow-up
  // repaint, but the synchronous write here means the very first
  // render after data arrives is already correct, so the user sees the
  // name appear in one frame rather than two. The standard
  // concurrent-rendering hazard with render-time ref writes (a render
  // gets discarded and the mutation leaks state forward) does not
  // apply: each render overwrites ``.current`` with the latest
  // directory, and readers only ever dereference ``byId.get(agentId)``
  // — they never observe the ref's identity, so a stale write from a
  // discarded render is harmlessly clobbered by the next render's
  // write.
  const directoryRef = React.useRef(agentDirectory);
  directoryRef.current = agentDirectory;

  // Empty dep array — built once, stable for the table's lifetime.
  const columns = React.useMemo(() => buildColumns(directoryRef), []);

  // Stable as long as ``columns`` is.
  const columnOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns.map((c) => ({
        id: colId(c),
        label: c.headerName ?? colId(c),
      })),
    [columns],
  );

  // Fields exposed in the filter widget. Must match the backend
  // whitelist ``SEARCH_FILTERABLE_FIELDS`` in ``agent_thread_pg_row.py``
  // — a field listed here but not on the server returns 400 on submit;
  // a field on the server but not here is unreachable from the UI.
  //
  // Selecting only threads you created uses the ``created_by`` filter
  // (set the value to your own user id). The filter widget is the
  // single source of truth for which slice of threads the table shows.
  //
  // ``visibility`` is a regular widget filter, default "no filter" =
  // "all visibilities". The wire values match the backend's
  // ``CHECK (visibility IN ('private', 'org'))`` constraint from
  // chats_019 via the ``ThreadVisibility`` enum colocated with
  // ``AgentThreadRecord``.
  //
  // ``Agent`` uses labeled enum options (``{ label: name, value: id }``)
  // so the dropdown shows friendly names while the wire value remains
  // the opaque ``agt_xxx`` id the backend filters on. The option list
  // is sourced from ``useAgentDirectory`` and reflows whenever the
  // directory finishes loading or picks up newly-created agents.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      { type: "string", field: "thread_id", name: "Thread ID" },
      { type: "string", field: "title", name: "Title" },
      { type: "string", field: "created_by", name: "Created by" },
      { type: "date", field: "created", name: "Created" },
      {
        type: "enum",
        field: "status",
        name: "Status",
        options: Object.values(AgentThreadStatus),
      },
      {
        type: "enum",
        field: "visibility",
        name: "Visibility",
        options: Object.values(ThreadVisibility),
      },
      // Known foundation-level limitation: ``type: "enum"`` comparators
      // are ``CONTAINS`` / ``NOT_CONTAINS``, which the backend renders
      // as ``LIKE '%value%'`` on scalar string columns (see
      // ``roboto_internal/persistence/postgres/query.py``). SQL ``_`` is
      // a single-character wildcard, and every agent id contains ``_``
      // at position 3 (``agt_<random>``), so the predicate is wider than
      // equality. Practical collision probability against opaque random
      // suffixes is effectively zero, but the right fix — equality on
      // the wire for opaque-id filters — affects every enum filter and
      // is out of scope here.
      {
        type: "enum",
        field: "created_from_agent_id",
        name: "Agent",
        options: agentDirectory.agents.map((a) => ({
          label: a.name,
          value: a.agent_id,
        })),
      },
    ],
    [agentDirectory.agents],
  );

  const tableState = useDataTableState<AgentThreadRecord>({
    getRowId: (row) => row.thread_id,
    initialSort: { columnId: "created", direction: "desc" },
    defaultVisibleColumnIds: columns.map(colId),
  });

  // The Agent cell renderer closes over ``directoryRef``, so when the
  // directory's contents change (initial load, agent created/renamed)
  // the existing rows still hold stale text until something repaints.
  // Force a targeted re-render of just the Agent column whenever the
  // ``byId`` map identity changes.
  const tableRef = tableState.table.tableRef;
  React.useEffect(() => {
    tableRef?.current?.refreshCells(["created_from_agent_id"]);
  }, [agentDirectory.byId, tableRef]);

  // Token chain for forward pagination. The entry at key ``N`` is the
  // ``after`` token to send when fetching page N (i.e. the
  // ``next_token`` returned on page N-1). Page 0 always sends no
  // ``after`` so it is not stored. The map is reset to empty whenever
  // the search shape changes (filters / sort / page size); the existing
  // ``useDataTableState`` already resets ``currentPage`` to 0 on those
  // events, so we mirror that here.
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    new Map(),
  );

  React.useEffect(() => {
    setTokenChain(new Map());
  }, [
    tableState.filters,
    tableState.matchMode,
    tableState.sort,
    tableState.pageSize,
  ]);

  // Build the wire body on every render. The hook's queryKey contains
  // ``query`` by value, so tanstack-query's structural equality check
  // suppresses a refetch unless filters, sort, page size, or the active
  // ``after`` token actually changed.
  //
  // ``composeFilters`` joins the caller-supplied ``scope`` with the
  // user's widget filters under an outer ``AND`` so the user can pick
  // ``OR`` match mode for their filters without the scope dissolving.
  const condition = composeFilters(
    scope ?? [],
    tableState.filters,
    tableState.matchMode,
  );
  const after =
    tableState.currentPage > 0
      ? tokenChain.get(tableState.currentPage)
      : undefined;
  const query: SearchQueryBody = {
    ...(tableState.sort && {
      sort_by: tableState.sort.columnId,
      sort_direction: tableState.sort.direction.toUpperCase() as SortDirection,
    }),
    ...(condition && { condition }),
    ...(after && { after }),
    limit: tableState.pageSize,
  };

  const activeQuery = useAgentThreadSearch({ orgId, query });

  // Cache the token for the page after the current one as soon as a
  // response arrives. Effect because writing to state during render
  // would cause an extra render; the dependency on ``data.next_token``
  // also covers the case where the same page is re-fetched (queryKey
  // change inside the same ``currentPage``) — the token for the next
  // page is re-stored, which is fine.
  const nextToken = activeQuery.data?.next_token ?? null;
  const currentPage = tableState.currentPage;
  React.useEffect(() => {
    if (nextToken === null) {
      return;
    }
    setTokenChain((prev) => {
      const existing = prev.get(currentPage + 1);
      if (existing === nextToken) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, nextToken);
      return next;
    });
  }, [nextToken, currentPage]);

  let loading: DataTableLoadingState = "idle";
  if (activeQuery.isLoading) {
    loading = "foreground";
  } else if (activeQuery.isFetching) {
    loading = "background";
  }

  const rows = activeQuery.data?.items ?? [];

  return (
    <>
      {activeQuery.isError ? (
        <Alert severity="error" className={styles.errorBanner}>
          Failed to load threads. {activeQuery.error?.message ?? ""}
        </Alert>
      ) : (
        <div className={styles.tableSlot}>
          <DataTable.Root>
            <DataTable.Actions>
              <DataTable.Filters
                filterDefinitions={filterDefinitions}
                onFilterSelected={tableState.addFilter}
              />
              <DataTable.ColumnVisibility
                options={columnOptions}
                visibleColumnIds={tableState.visibleColumnIds}
                setColumnVisibility={tableState.setColumnVisibility}
              />
              {trailingActions ? (
                <div className={styles.trailingActions}>{trailingActions}</div>
              ) : null}
            </DataTable.Actions>
            <DataTable.ActiveFilters
              {...tableState.activeFilters}
              filterDefinitions={filterDefinitions}
            />
            <DataTable.Table<AgentThreadRecord>
              {...tableState.table}
              hasNextPage={Boolean(activeQuery.data?.next_token)}
              loading={loading}
              columns={columns}
              data={rows}
              noRowsContent={noRowsContent ?? "No threads yet."}
            />
          </DataTable.Root>
        </div>
      )}
    </>
  );
}
